import { useAssistantStatusQuery } from "@/actions/assistant.actions";
import { useLogout } from "@/actions/user.actions";
import { CrmBrand, useCrmBranding } from "@/components/CrmBranding";
import { useWhatsAppAccountsQuery, useWhatsAppStatusQuery } from "@/actions/whatsapp.actions";
import { canAccessCampaigns, canAccessOrg, canAccessReports, canManageContacts } from "@/lib/permissions";
import { useAuthStore } from "@/states/auth";
import { NotificationsDropdown } from "../Notifications";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import type { MenuProps } from "antd";
import { Dropdown } from "antd";
import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  Building2,
  CalendarDays,
  ChevronDown,
  CreditCard,
  FileText,
  LogOut,
  Mail,
  MapPin,
  Megaphone,
  MessageCircle,
  MenuIcon,
  PackageIcon,
  Settings2,
  SparklesIcon,
  UserRound,
  UsersRound,
  XIcon,
} from "lucide-react";
import { useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";
import { ProductPageProvider } from "../ProductPage";

type NavigationItem = {
  name: string;
  href: string;
  children?: Array<{ name: string; href: string }>;
};

const baseNavigation: NavigationItem[] = [
  {
    name: "Quotes",
    href: "itineraries",
    children: [
      { name: "Itineraries", href: "itineraries" },
    ],
  },
  { name: "Packages", href: "itinerary-templates" },
  {
    name: "Leads",
    href: "leads",
    children: [{ name: "Leads", href: "leads" }],
  },
  { name: "Payments", href: "payments" },
  { name: "Trip Calendar", href: "leads/calendar" },
];

const superAdminNavigation: NavigationItem[] = [
  { name: "Dashboard", href: "admin/dashboard" },
  { name: "Tenants", href: "admin/tenants" },
  { name: "Locations", href: "admin/locations" },
  { name: "AI keys", href: "admin/ai-keys" },
];

const mobileNavigationIcons: Record<string, LucideIcon> = {
  Dashboard: BarChart3,
  Emails: Mail,
  WhatsApp: MessageCircle,
  Quotes: FileText,
  Packages: PackageIcon,
  Leads: UsersRound,
  Payments: CreditCard,
  "Trip Calendar": CalendarDays,
  Campaigns: Megaphone,
  "AI Assistant": SparklesIcon,
  Reports: BarChart3,
  Tenants: Building2,
  Users: UserRound,
  Locations: MapPin,
};

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "TB";

const formatRole = (role: string) =>
  role
    .split("_")
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");

const normalizePath = (href: string) => `/${href.replace(/^\//, "")}`;

const isPathActive = (pathname: string, href: string) => {
  const normalizedHref = normalizePath(href);
  return pathname === normalizedHref || pathname.startsWith(`${normalizedHref}/`);
};

const getNavigationMatchScore = (pathname: string, item: NavigationItem) =>
  [item.href, ...(item.children?.map((child) => child.href) ?? [])].reduce((highestScore, href) => {
    if (!isPathActive(pathname, href)) {
      return highestScore;
    }

    return Math.max(highestScore, normalizePath(href).length);
  }, -1);

const desktopNavClassName = (isActive: boolean) =>
  [
    "relative inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium no-underline",
    "transition-[background-color,color,transform] duration-150 focus-visible:outline-none focus-visible:ring-2",
    "focus-visible:ring-(--brand-primary)/50 active:scale-[0.98]",
    isActive
      ? "!border-(--brand-secondary) !bg-(--brand-secondary) !text-[var(--brand-secondary-foreground)] shadow-[0_1px_2px_rgba(19,27,42,0.12)] hover:!border-(--brand-secondary-hover) hover:!bg-(--brand-secondary-hover) hover:!text-[var(--brand-secondary-foreground)]"
      : "!border-transparent !bg-transparent !text-[#131B2A]/70 hover:!border-gray-200 hover:!bg-gray-50 hover:!text-[#131B2A]",
  ].join(" ");

const mobileNavClassName = (isActive: boolean) =>
  [
    "flex min-h-12 w-full items-center gap-3 rounded-xl px-3.5 text-sm font-semibold no-underline transition-colors duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)/50",
    isActive
      ? "!bg-(--brand-primary)/10 !text-[#805A19] hover:!bg-(--brand-primary)/15"
      : "!bg-transparent !text-[#131B2A]/75 hover:!bg-gray-50 hover:!text-[#131B2A]",
  ].join(" ");

const mobileChildNavClassName = (isActive: boolean) =>
  [
    "flex min-h-10 items-center rounded-lg px-3.5 text-sm font-medium no-underline transition-colors duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)/50",
    isActive
      ? "!bg-(--brand-primary)/10 !text-[#805A19]"
      : "!bg-transparent !text-[#131B2A]/65 hover:!bg-gray-50 hover:!text-[#131B2A]",
  ].join(" ");

/**
 * Main navigation bar for authenticated users.
 */
export const Navbar = () => {
  const user = useAuthStore((state) => state.user);
  const authSnapshot = useAuthStore((state) => state.authSnapshot);
  const { refetch: logout } = useLogout();
  const { branding } = useCrmBranding();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // The assistant entry only appears once the server confirms it is enabled for this organisation.
  const isSuperAdminShell = user?.role === "super_admin" && !authSnapshot;
  const assistantStatus = useAssistantStatusQuery(Boolean(user) && !isSuperAdminShell);
  const assistantAvailable = assistantStatus.data?.available === true;
  const whatsappStatus = useWhatsAppStatusQuery(Boolean(user) && !isSuperAdminShell);
  const whatsappEnabled = Boolean(user) && !isSuperAdminShell && !whatsappStatus.isError
    && whatsappStatus.data?.enabled === true && whatsappStatus.data.configured;
  const whatsappAccounts = useWhatsAppAccountsQuery(whatsappEnabled, "connected");
  const whatsappAvailable = whatsappEnabled && !whatsappAccounts.isError
    && (whatsappAccounts.data?.data.length ?? 0) > 0;

  const navigation = useMemo(() => {
    if (user?.role === "super_admin" && !authSnapshot) {
      return superAdminNavigation;
    }

    const items = baseNavigation.map((item) => ({
      ...item,
      children: item.children ? [...item.children] : undefined,
    }));

    if (canManageContacts(user?.role)) {
      const leadsItem = items.find((item) => item.name === "Leads");
      if (leadsItem?.children && !leadsItem.children.find((child) => child.href === "contacts")) {
        leadsItem.children.push({ name: "Contacts", href: "contacts" });
      }
    }

    if (canAccessOrg(user?.role)) {
      const leadsItem = items.find((item) => item.name === "Leads");
      if (leadsItem?.children && !leadsItem.children.find((child) => child.href === "agencies")) {
        leadsItem.children.splice(1, 0, { name: "Agencies", href: "org/agencies" });
      }
    }

    if (user?.mailboxNavVisible) {
      items.unshift({ name: "Emails", href: "mailbox", children: undefined });
    }

    if (whatsappAvailable) {
      const emailIndex = items.findIndex((item) => item.href === "mailbox");
      items.splice(emailIndex + 1, 0, { name: "WhatsApp", href: "whatsapp", children: undefined });
    }

    if (canAccessCampaigns(user?.role) && !items.find((item) => item.href === "campaigns")) {
      items.push({ name: "Campaigns", href: "campaigns", children: undefined });
    }

    if (assistantAvailable && !items.find((item) => item.href === "assistant")) {
      items.push({ name: "AI Assistant", href: "assistant", children: undefined });
    }

    if (canAccessReports(user?.role) && !items.find((item) => item.href === "reports")) {
      items.push({ name: "Reports", href: "reports", children: undefined });
    }

    return items;
  }, [assistantAvailable, authSnapshot, user?.mailboxNavVisible, user?.role, whatsappAvailable]);

  const activeNavigationItemName = useMemo(
    () =>
      navigation.reduce(
        (activeItem, item) => {
          const score = getNavigationMatchScore(pathname, item);
          return score > activeItem.score ? { name: item.name, score } : activeItem;
        },
        { name: "", score: -1 },
      ).name,
    [navigation, pathname],
  );

  return (
    <ProductPageProvider>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-gray-200 bg-white text-[#131B2A] shadow-[0_1px_2px_rgba(19,27,42,0.04)]">
        <Disclosure as="nav" aria-label="Primary navigation">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="relative flex h-16 items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-4">
                <DisclosureButton className="group inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-[#131B2A] transition-colors hover:border-(--brand-primary)/50 hover:bg-[#FBFBFB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)/50 lg:hidden">
                  <span className="sr-only">Toggle main menu</span>
                  <MenuIcon aria-hidden="true" className="h-5 w-5 group-data-[open]:hidden" strokeWidth={1.8} />
                  <XIcon aria-hidden="true" className="hidden h-5 w-5 group-data-[open]:block" strokeWidth={1.8} />
                </DisclosureButton>

                <NavLink
                  to="/"
                  aria-label={`${branding?.name || "TripboxCRM"} dashboard`}
                  className="absolute left-1/2 inline-flex -translate-x-1/2 items-center no-underline lg:static lg:translate-x-0"
                >
                  <CrmBrand className="h-8 max-w-48 object-contain" />
                </NavLink>

                {user?.tenant?.name ? (
                  <div className="hidden min-w-0 items-center gap-2 border-l border-gray-200 pl-4 xl:flex">
                    <Building2 className="h-3.5 w-3.5 shrink-0 text-(--brand-primary)" strokeWidth={1.8} />
                    <span className="max-w-36 truncate text-xs font-medium text-gray-500">{user.tenant.name}</span>
                  </div>
                ) : null}
              </div>

              {user ? (
                <div className="hidden min-w-0 flex-1 items-center justify-center gap-1 lg:flex">
                  {navigation.map((item) => {
                    const itemIsActive = activeNavigationItemName === item.name;
                    const hasDropdown = Boolean(item.children?.length);

                    if (hasDropdown) {
                      const menuItems: MenuProps["items"] = item.children?.map((child) => ({
                        key: child.href,
                        label: (
                          <NavLink
                            to={child.href}
                            className={({ isActive }) =>
                              [
                                "flex min-h-9 items-center rounded-md px-2 text-sm font-medium no-underline transition-colors",
                                isActive
                                  ? "!bg-(--brand-secondary) !text-[var(--brand-secondary-foreground)] hover:!bg-(--brand-secondary-hover)"
                                  : "!bg-transparent !text-[#131B2A]/75 hover:!bg-gray-50 hover:!text-[#131B2A]",
                              ].join(" ")
                            }
                          >
                            {child.name}
                          </NavLink>
                        ),
                      }));

                      return (
                        <Dropdown
                          key={item.name}
                          menu={{ items: menuItems, selectable: false }}
                          trigger={["hover", "click"]}
                          placement="bottom"
                          rootClassName="[&_.ant-dropdown-menu]:!rounded-xl [&_.ant-dropdown-menu]:!border [&_.ant-dropdown-menu]:!border-gray-200 [&_.ant-dropdown-menu]:!p-1.5 [&_.ant-dropdown-menu]:!shadow-[0_12px_32px_rgba(19,27,42,0.10)] [&_.ant-dropdown-menu-item]:!rounded-lg [&_.ant-dropdown-menu-item]:!p-0"
                        >
                          <button
                            type="button"
                            className={desktopNavClassName(itemIsActive)}
                            onClick={() => navigate(item.href)}
                            aria-current={itemIsActive ? "page" : undefined}
                            data-navbar-state={itemIsActive ? "active" : "inactive"}
                            data-navbar-item={item.name}
                          >
                            {itemIsActive ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
                            {item.name}
                            <ChevronDown className="h-3.5 w-3.5 opacity-60" strokeWidth={1.8} />
                          </button>
                        </Dropdown>
                      );
                    }

                    return (
                      <NavLink
                        key={item.name}
                        to={item.href}
                        className={() => desktopNavClassName(itemIsActive)}
                        aria-current={itemIsActive ? "page" : undefined}
                        data-navbar-state={itemIsActive ? "active" : "inactive"}
                        data-navbar-item={item.name}
                      >
                        {itemIsActive ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
                        {item.name}
                      </NavLink>
                    );
                  })}
                </div>
              ) : null}

              <div className="flex shrink-0 items-center justify-end gap-2">
                {user ? (
                  <>
                    {!isSuperAdminShell && <NotificationsDropdown />}
                    <Menu as="div" className="relative">
                    <MenuButton className="group flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white p-1.5 pr-2 text-sm transition-[border-color,background-color,transform] duration-150 hover:border-(--brand-primary)/50 hover:bg-[#FBFBFB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)/50 active:scale-[0.98]">
                      <span className="sr-only">Open account menu</span>
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#131B2A] text-xs font-semibold tracking-tight text-white">
                        {getInitials(user.fullname)}
                      </span>
                      <span className="hidden max-w-32 truncate text-left text-xs font-semibold text-[#131B2A] xl:block">
                        {user.fullname}
                      </span>
                      <ChevronDown
                        className="hidden h-3.5 w-3.5 text-gray-400 transition-transform duration-150 group-data-[open]:rotate-180 sm:block"
                        strokeWidth={1.8}
                      />
                    </MenuButton>

                    <MenuItems
                      transition
                      className="absolute right-0 z-10 mt-2 w-72 origin-top-right overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_12px_32px_rgba(19,27,42,0.10)] transition focus:outline-none data-[closed]:scale-[0.98] data-[closed]:opacity-0 data-[enter]:duration-150 data-[leave]:duration-100"
                    >
                      <div className="border-b border-gray-200 bg-[#FBFBFB] p-4">
                        <div className="flex items-center gap-3">
                          <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#131B2A] text-xs font-semibold text-white">
                            {getInitials(user.fullname)}
                            <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-[#FBFBFB] bg-(--brand-primary)" />
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-[#131B2A]">{user.fullname}</p>
                            <p className="mt-0.5 truncate text-xs text-gray-500">{user.email}</p>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-200 pt-3 text-[11px]">
                          <span className="truncate font-medium text-gray-500">{user.tenant?.name}</span>
                          <span className="shrink-0 rounded-md border border-(--brand-primary)/35 bg-(--brand-primary)/10 px-2 py-0.5 font-semibold text-[#805A19]">
                            {formatRole(user.role)}
                          </span>
                        </div>
                      </div>

                      <div className="p-1.5">
                        <MenuItem>
                          <NavLink
                            to="/profile"
                            className="flex min-h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium !text-[#131B2A]/75 no-underline transition-colors data-[focus]:bg-gray-50 data-[focus]:!text-[#131B2A]"
                          >
                            <UserRound className="h-4 w-4 text-(--brand-primary)" strokeWidth={1.8} />
                            Profile & security
                          </NavLink>
                        </MenuItem>
                        {canAccessOrg(user.role) ? (
                          <MenuItem>
                            <NavLink
                              to="/org"
                              className="flex min-h-10 items-center gap-3 rounded-lg px-3 text-sm font-medium !text-[#131B2A]/75 no-underline transition-colors data-[focus]:bg-gray-50 data-[focus]:!text-[#131B2A]"
                            >
                              <Settings2 className="h-4 w-4 text-(--brand-primary)" strokeWidth={1.8} />
                              Organisation settings
                            </NavLink>
                          </MenuItem>
                        ) : null}
                      </div>

                      <div className="border-t border-gray-200 p-1.5">
                        <MenuItem>
                          <button
                            type="button"
                            className="flex min-h-10 w-full items-center gap-3 rounded-lg px-3 text-left text-sm font-medium text-red-600 transition-colors data-[focus]:bg-red-50"
                            onClick={() => logout()}
                          >
                            <LogOut className="h-4 w-4" strokeWidth={1.8} />
                            Sign out
                          </button>
                        </MenuItem>
                      </div>
                    </MenuItems>
                  </Menu>
                </>
                ) : (
                  <NavLink
                    to="/login"
                    className="inline-flex h-10 items-center rounded-lg bg-[#131B2A] px-4 text-sm font-semibold !text-white no-underline transition-colors hover:bg-[#0F1624] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)/50"
                  >
                    Sign in
                  </NavLink>
                )}
              </div>
            </div>
          </div>

          <DisclosurePanel className="fixed inset-0 z-[60] lg:hidden">
            {({ close }) => (
              <>
                <DisclosureButton
                  aria-label="Close navigation menu"
                  className="absolute inset-0 cursor-default bg-[#131B2A]/35 backdrop-blur-[2px]"
                />
                <div className="relative flex h-full w-[min(88vw,22rem)] flex-col bg-white shadow-[12px_0_32px_rgba(19,27,42,0.16)]">
                  <div className="flex justify-end px-4 pt-4">
                    <DisclosureButton className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-[#131B2A] shadow-[0_1px_2px_rgba(19,27,42,0.06)] transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)/50">
                      <span className="sr-only">Close navigation menu</span>
                      <XIcon aria-hidden="true" className="h-5 w-5" strokeWidth={1.8} />
                    </DisclosureButton>
                  </div>

                  <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-6 pt-5">
                    <div className="space-y-2">
                      {navigation.map((item) => {
                        const itemIsActive = activeNavigationItemName === item.name;
                        const Icon = mobileNavigationIcons[item.name] ?? FileText;

                        return (
                          <div key={item.name}>
                            <NavLink
                              to={item.href}
                              onClick={() => close()}
                              className={() => mobileNavClassName(itemIsActive)}
                              aria-current={itemIsActive ? "page" : undefined}
                              data-navbar-state={itemIsActive ? "active" : "inactive"}
                              data-navbar-item={item.name}
                            >
                              <span
                                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                                  itemIsActive ? "bg-(--brand-primary)/15" : "bg-gray-100"
                                }`}
                              >
                                <Icon aria-hidden="true" className="h-4 w-4" strokeWidth={1.9} />
                              </span>
                              <span className="flex-1">{item.name}</span>
                            </NavLink>
                            {item.children?.length ? (
                              <div className="ml-7 mt-1 border-l border-gray-200 py-1 pl-3">
                                {item.children.map((child) => {
                                  const childIsActive = isPathActive(pathname, child.href);

                                  return (
                                    <NavLink
                                      key={child.href}
                                      to={child.href}
                                      onClick={() => close()}
                                      className={() => mobileChildNavClassName(childIsActive)}
                                      aria-current={childIsActive ? "page" : undefined}
                                    >
                                      {child.name}
                                    </NavLink>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}
          </DisclosurePanel>
        </Disclosure>
      </header>
    </ProductPageProvider>
  );
};
